import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Select,
  FormControl,
  Chip,
  Snackbar,
  Checkbox,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MuiAlert from '@mui/material/Alert';
import LoadingBar from 'react-top-loading-bar';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const TopicProgresss = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const drawerWidth = 240;

  // State for dropdowns
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');

  // State for dropdown data
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);

  // State for topics and UI
  const [topics, setTopics] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [remarkInputs, setRemarkInputs] = useState({});
  const [checkedTopics, setCheckedTopics] = useState({});
  const [originalTopics, setOriginalTopics] = useState([]);
  const [topicStatuses, setTopicStatuses] = useState({});
  const [pendingTopicsWithRemarks, setPendingTopicsWithRemarks] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [changedTopics, setChangedTopics] = useState({});

  // Fetch Teacher Data
  const fetchTeacherData = async () => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/AssignmentCreate/GetTeacher`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({}),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setTeachers(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch teacher data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setTeachers([]);
      setLoadingBarProgress(0);
    }
  };

  // Fetch Class Data
  const fetchClassData = async (teacherId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/AssignmentCreate/GetClass`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ teacherId }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setClasses(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch class data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setClasses([]);
      setLoadingBarProgress(0);
    }
  };

  // Fetch Section Data
  const fetchSectionData = async (classId, teacherId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/AssignmentCreate/ddlSection_clsId`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ classId, teacherId }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setSections(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch section data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setSections([]);
      setLoadingBarProgress(0);
    }
  };

  // Fetch Subject Data
  const fetchSubjectData = async (classId, teacherId, sectionId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/AssignmentCreate/Subjectddl`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ classId, teacherId, sectionId }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setSubjects(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch subject data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setSubjects([]);
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setLoadingBarProgress(0);
    }
  };

  // Helper function to extract remark from topic
  const extractRemarkFromTopic = (topic) => {
    if (topic.coveredDescription) return topic.coveredDescription;
    if (topic.remarks) return topic.remarks;
    if (topic.remark) return topic.remark;
    if (topic.description) return topic.description;
    if (topic.note) return topic.note;
    if (topic.notes) return topic.notes;
    if (topic.comment) return topic.comment;
    if (topic.comments) return topic.comments;

    const stringFields = Object.keys(topic).filter(key => {
      const isString = typeof topic[key] === 'string';
      const keyLower = key.toLowerCase();
      const containsRemarkWord = (
        keyLower.includes('remark') ||
        keyLower.includes('desc') ||
        keyLower.includes('note') ||
        keyLower.includes('comment')
      );
      return isString && containsRemarkWord;
    });

    if (stringFields.length > 0) {
      return topic[stringFields[0]];
    }

    return '';
  };

  // Fetch Topics Data
  const fetchTopicsData = useCallback(async (classId, sectionId, subjectId, preserveCheckedState = false) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/TeachingTopics/GetByClassSubject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ classId, sectionId, subjectId }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        const newTopics = responseData.topics || [];

        setTopics(newTopics);
        setFilteredTopics(newTopics);
        setOriginalTopics(newTopics);
        setChangedTopics({});

        const statuses = {};
        newTopics.forEach(topic => {
          statuses[topic.topicId] = topic.status;
        });
        setTopicStatuses(statuses);

        const initialRemarks = {};
        const initialChecked = {};

        if (newTopics && Array.isArray(newTopics)) {
          newTopics.forEach(topic => {
            initialRemarks[topic.topicId] = extractRemarkFromTopic(topic);
            initialChecked[topic.topicId] = (topic.status === 'C');
          });
        }

        setRemarkInputs(initialRemarks);
        setCheckedTopics(initialChecked);
        setPendingTopicsWithRemarks([]);

        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch topics');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setLoadingBarProgress(0);
    }
  }, []);

  const handleStatusChange = (topicId, newStatus) => {
    const currentStatus = topicStatuses[topicId];

    if (currentStatus !== newStatus) {
      setTopicStatuses(prev => ({
        ...prev,
        [topicId]: newStatus,
      }));

      setChangedTopics(prev => ({
        ...prev,
        [topicId]: true
      }));

      if (newStatus === 'C') {
        setCheckedTopics(prev => ({
          ...prev,
          [topicId]: true
        }));
      } else {
        setCheckedTopics(prev => ({
          ...prev,
          [topicId]: false
        }));
      }
    }
  };

  // Filter topics based on search text - ONLY BY TOPIC NAME
  const handleSearch = () => {
    if (!searchText.trim()) {
      setFilteredTopics(topics);
      return;
    }

    const searchTerm = searchText.toLowerCase().trim();
    const filtered = topics.filter(topic =>
      topic.topicName?.toLowerCase().includes(searchTerm)
    );

    setFilteredTopics(filtered);
  };

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (!value.trim()) {
      setFilteredTopics(topics);
    } else {
      const searchTerm = value.toLowerCase().trim();
      const filtered = topics.filter(topic =>
        topic.topicName?.toLowerCase().includes(searchTerm)
      );
      setFilteredTopics(filtered);
    }
  };

  // Handle search on Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search when new subject is selected
  useEffect(() => {
    if (selectedSubject && selectedSection && selectedClass && selectedTeacher) {
      setSearchText('');
      setFilteredTopics(topics);
    }
  }, [selectedSubject, selectedSection, selectedClass, selectedTeacher, topics]);

  // Fetch Teacher Data on Component Mount
  useEffect(() => {
    fetchTeacherData();
  }, []);

  // Fetch Class Data when Teacher is Selected
  useEffect(() => {
    if (selectedTeacher) {
      fetchClassData(selectedTeacher.employeeId);
      setSelectedClass('');
      setSelectedSection('');
      setSelectedSubject('');
      setSearchText('');
      setFilteredTopics([]);
      setChangedTopics({});
    }
  }, [selectedTeacher]);

  // Fetch Section Data when Class is Selected
  useEffect(() => {
    if (selectedClass && selectedTeacher) {
      fetchSectionData(selectedClass.classId, selectedTeacher.employeeId);
      setSelectedSection('');
      setSelectedSubject('');
      setSearchText('');
      setFilteredTopics([]);
      setChangedTopics({});
    }
  }, [selectedClass, selectedTeacher]);

  // Fetch Subject Data when Section is Selected
  useEffect(() => {
    if (selectedSection && selectedClass && selectedTeacher) {
      fetchSubjectData(
        selectedClass.classId,
        selectedTeacher.employeeId,
        selectedSection.sectionId
      );
      setSelectedSubject('');
      setSearchText('');
      setFilteredTopics([]);
      setChangedTopics({});
    }
  }, [selectedSection, selectedClass, selectedTeacher]);

  // Fetch Topics when Subject is Selected
  useEffect(() => {
    if (selectedSubject && selectedSection && selectedClass && selectedTeacher) {
      fetchTopicsData(
        selectedClass.classId,
        selectedSection.sectionId,
        selectedSubject.subjectId,
        false
      );
      setSearchText('');
      setChangedTopics({});
    }
  }, [selectedSubject, selectedSection, selectedClass, selectedTeacher]);

  // Handle remark input change
  const handleRemarkChange = (topicId, value) => {
    const currentRemark = remarkInputs[topicId] || '';
    const originalRemark = extractRemarkFromTopic(topics.find(t => t.topicId === topicId)) || '';

    if (currentRemark !== value) {
      setRemarkInputs(prev => ({
        ...prev,
        [topicId]: value
      }));

      if (value !== originalRemark) {
        setChangedTopics(prev => ({
          ...prev,
          [topicId]: true
        }));
      }
    }

    const topic = topics.find(t => t.topicId === topicId);
    if (topic && topic.status === 'P' && value.trim() !== '') {
      setPendingTopicsWithRemarks(prev => {
        if (!prev.includes(topicId)) {
          return [...prev, topicId];
        }
        return prev;
      });
    } else {
      setPendingTopicsWithRemarks(prev => prev.filter(id => id !== topicId));
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (topicId) => {
    const newCheckedState = !checkedTopics[topicId];
    setCheckedTopics(prev => ({
      ...prev,
      [topicId]: newCheckedState
    }));

    setChangedTopics(prev => ({
      ...prev,
      [topicId]: true
    }));

    const newStatus = newCheckedState ? 'C' : 'P';
    setTopicStatuses(prev => ({
      ...prev,
      [topicId]: newStatus
    }));

    if (newCheckedState) {
      setPendingTopicsWithRemarks(prev => prev.filter(id => id !== topicId));
    }
  };

  // Save Remarks and Checkbox Status
  const handleSave = async () => {
    try {
      if (!selectedTeacher || !selectedClass || !selectedSection || !selectedSubject) {
        setSnackbarMessage('Please select all filters');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        return;
      }

      const changedTopicIds = Object.keys(changedTopics).filter(id => changedTopics[id]);

      if (changedTopicIds.length === 0) {
        setSnackbarMessage('No changes to save');
        setOpenSnackbar(true);
        setSnackbarSeverity('warning');
        return;
      }

      const topicsToSave = changedTopicIds.map(topicId => {
        const topic = topics.find(t => t.topicId === Number(topicId) || t.topicId === topicId);
        return {
          topicId: topicId,
          status: topicStatuses[topicId] || topic.status,
          coveredby_TeacherId: selectedTeacher.employeeId,
          coveredby_TeacherName: selectedTeacher.employeeName,
          description: remarkInputs[topicId] || extractRemarkFromTopic(topic),
        };
      });

      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/TeachingTopics/MarkCovered`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ topics: topicsToSave }),
        }
      );

      setLoadingBarProgress(100);

      if (response.ok) {
        setSnackbarMessage(`Saved successfully for ${changedTopicIds.length} topic(s)`);
        setOpenSnackbar(true);
        setSnackbarSeverity('success');
        setChangedTopics({});

        setTimeout(() => {
          fetchTopicsData(
            selectedClass.classId,
            selectedSection.sectionId,
            selectedSubject.subjectId,
            false
          );
        }, 500);
      } else {
        setSnackbarMessage('Failed to save changes');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setLoadingBarProgress(0);
    }
  };

  // Handle Cancel
  const handleCancel = () => {
    const changedTopicIds = Object.keys(changedTopics);

    if (changedTopicIds.length === 0) {
      setSnackbarMessage('No changes to cancel');
      setOpenSnackbar(true);
      setSnackbarSeverity('info');
      return;
    }

    const updatedStatuses = { ...topicStatuses };
    const updatedRemarks = { ...remarkInputs };
    const updatedChecked = { ...checkedTopics };

    changedTopicIds.forEach(topicId => {
      const topic = topics.find(t => t.topicId === Number(topicId) || t.topicId === topicId);
      if (topic) {
        updatedStatuses[topicId] = topic.status;
        updatedRemarks[topicId] = extractRemarkFromTopic(topic);
        updatedChecked[topicId] = (topic.status === 'C');
      }
    });

    setTopicStatuses(updatedStatuses);
    setRemarkInputs(updatedRemarks);
    setCheckedTopics(updatedChecked);
    setChangedTopics({});
    setPendingTopicsWithRemarks([]);

    setSnackbarMessage('Changes cancelled');
    setOpenSnackbar(true);
    setSnackbarSeverity('info');
  };

  // Handle dropdown changes
  const handleTeacherChange = (e) => {
    const selected = teachers.find((t) => t.employeeId === e.target.value);
    setSelectedTeacher(selected);
  };

  const handleClassChange = (e) => {
    const selected = classes.find((c) => c.classId === e.target.value);
    setSelectedClass(selected);
  };

  const handleSectionChange = (e) => {
    const selected = sections.find((s) => s.sectionId === e.target.value);
    setSelectedSection(selected);
  };

  const handleSubjectChange = (e) => {
    const selected = subjects.find((sub) => sub.subjectId === e.target.value);
    setSelectedSubject(selected);
  };

  // Display topics - use filteredTopics when search is active, otherwise use all topics
  const displayTopics = searchText.trim() ? filteredTopics : topics;

  // Calculate responsive widths based on screen size
  const getFilterWidths = () => {
    if (isExtraSmallScreen) {
      return {
        search: '100%',
        teacher: '100%',
        class: '100%',
        section: '100%',
        subject: '100%',
        gap: 1,
      };
    }
    if (isVerySmallScreen) {
      return {
        search: '48%',
        teacher: '48%',
        class: '48%',
        section: '48%',
        subject: '48%',
        gap: 1,
      };
    }
    if (isSmallScreen) {
      return {
        search: '19%',
        teacher: '19%',
        class: '19%',
        section: '19%',
        subject: '19%',
        gap: 1,
      };
    }
    return {
      search: '18%',
      teacher: '18%',
      class: '14%',
      section: '14%',
      subject: '18%',
      gap: 2,
    };
  };

  const filterWidths = getFilterWidths();

  return (
    <Box
      component="main"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 0, sm: '64px' },
        p: 3,
        backgroundColor: '#f4f6f9',
        minHeight: '100vh',
      }}
    >
      <AppBar position="static" style={{ backgroundColor: '#063552' }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Topic Progress
          </Typography>
        </Toolbar>
      </AppBar>
      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>
        <LoadingBar progress={loadingBarProgress} color="rgb(152, 106, 182)" height={5} />

        {/* Responsive Filters Container */}
        <Box sx={{
          display: 'flex',
          flexDirection: isExtraSmallScreen ? 'column' : 'row',
          gap: filterWidths.gap,
          mb: 3,
          width: '100%',
          flexWrap: isVerySmallScreen && !isExtraSmallScreen ? 'wrap' : 'nowrap',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          mt: 2
        }}>
          {/* Search Bar */}
          <Box sx={{
            width: isExtraSmallScreen ? filterWidths.search : filterWidths.search,
            minWidth: isExtraSmallScreen ? '100%' : '150px'
          }}>
            <TextField
              placeholder="Search by topic name..."
              variant="outlined"
              size="small"
              value={searchText}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              fullWidth
              disabled={!selectedSubject}
              InputProps={{
                sx: { height: isExtraSmallScreen ? '40px' : 'auto' },
                endAdornment: (
                  <IconButton
                    onClick={handleSearch}
                    size="small"
                    disabled={!selectedSubject}
                  >
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>

          {/* Teacher Dropdown */}
          <Box sx={{
            width: isExtraSmallScreen ? filterWidths.teacher : filterWidths.teacher,
            minWidth: isExtraSmallScreen ? '100%' : '150px'
          }}>
            <FormControl fullWidth size="small">
              <Select
                value={selectedTeacher?.employeeId || ''}
                onChange={handleTeacherChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#757575' }}>Teacher</span>;
                  }
                  const teacher = teachers.find(t => t.employeeId === selected);
                  return teacher?.employeeName || selected;
                }}
              >
                <MenuItem value="" disabled>
                  Select Teacher
                </MenuItem>
                {teachers.map((teacher) => (
                  <MenuItem key={teacher.employeeId} value={teacher.employeeId}>
                    {teacher.employeeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Class Dropdown */}
          <Box sx={{
            width: isExtraSmallScreen ? filterWidths.class : filterWidths.class,
            minWidth: isExtraSmallScreen ? '100%' : '120px'
          }}>
            <FormControl fullWidth size="small" disabled={!selectedTeacher}>
              <Select
                value={selectedClass?.classId || ''}
                onChange={handleClassChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#757575' }}>Class</span>;
                  }
                  const cls = classes.find(c => c.classId === selected);
                  return cls?.className || selected;
                }}
              >
                <MenuItem value="" disabled>
                  Select Class
                </MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.classId} value={cls.classId}>
                    {cls.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Section Dropdown */}
          <Box sx={{
            width: isExtraSmallScreen ? filterWidths.section : filterWidths.section,
            minWidth: isExtraSmallScreen ? '100%' : '120px'
          }}>
            <FormControl fullWidth size="small" disabled={!selectedClass}>
              <Select
                value={selectedSection?.sectionId || ''}
                onChange={handleSectionChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#757575' }}>Section</span>;
                  }
                  const section = sections.find(s => s.sectionId === selected);
                  return section?.sectionName || selected;
                }}
              >
                <MenuItem value="" disabled>
                  Select Section
                </MenuItem>
                {sections.map((section) => (
                  <MenuItem key={section.sectionId} value={section.sectionId}>
                    {section.sectionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Subject Dropdown */}
          <Box sx={{
            width: isExtraSmallScreen ? filterWidths.subject : filterWidths.subject,
            minWidth: isExtraSmallScreen ? '100%' : '150px'
          }}>
            <FormControl fullWidth size="small" disabled={!selectedSection}>
              <Select
                value={selectedSubject?.subjectId || ''}
                onChange={handleSubjectChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#757575' }}>Subject</span>;
                  }
                  const subject = subjects.find(s => s.subjectId === selected);
                  return subject?.subjectName || selected;
                }}
              >
                <MenuItem value="" disabled>
                  Select Subject
                </MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject.subjectId} value={subject.subjectId}>
                    {subject.subjectName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Topics Table */}
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{
            minWidth: 650,
            border: '1px solid #ccc',
            '& .MuiTableCell-root': {
              padding: isExtraSmallScreen ? '8px' : '16px'
            }
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>Topic Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>Remark</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', borderRight: '1px solid #ccc' }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayTopics.length > 0 ? (
                displayTopics.map((topic) => (
                  <TableRow
                    key={topic.topicId}
                    sx={{
                      borderTop: '1px solid #ccc'
                    }}
                  >
                    <TableCell sx={{ borderRight: '1px solid #ccc' }}>
                      {topic.topicName}
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc' }}>
                      <Chip
                        label={topicStatuses[topic.topicId] === 'P' ? 'Pending' : 'Covered'}
                        color={topicStatuses[topic.topicId] === 'C' ? 'success' : 'error'}
                        sx={{ fontWeight: 'bold' }}
                        size={isExtraSmallScreen ? "small" : "medium"}
                      />
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc' }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size={isExtraSmallScreen ? "small" : "small"}
                        value={remarkInputs[topic.topicId] || ''}
                        onChange={(e) => handleRemarkChange(topic.topicId, e.target.value)}
                        placeholder="Add remark..."
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #ccc' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          variant={topicStatuses[topic.topicId] === 'P' ? 'contained' : 'outlined'}
                          color="error"
                          size={isExtraSmallScreen ? "small" : "medium"}
                          onClick={() => handleStatusChange(topic.topicId, 'P')}
                        >
                          Pending
                        </Button>
                        <Button
                          variant={topicStatuses[topic.topicId] === 'C' ? 'contained' : 'outlined'}
                          color="success"
                          size={isExtraSmallScreen ? "small" : "medium"}
                          onClick={() => handleStatusChange(topic.topicId, 'C')}
                        >
                          Covered
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    {searchText.trim() ?
                      `No topics found matching "${searchText}"` :
                      'No topics available. Please select a subject first.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Save and Cancel Buttons */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 3,
          flexWrap: 'wrap'
        }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCancel}
            disabled={!selectedSubject}
            size={isExtraSmallScreen ? "small" : "medium"}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!selectedSubject}
            size={isExtraSmallScreen ? "small" : "medium"}
          >
            Save
          </Button>
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default TopicProgresss;